import { useQuery } from "@tanstack/react-query";
import { useLocationStore } from "@/lib/store";
import type { Location, AssistantTask, Citation, Review } from "@shared/schema";

export interface Recommendation {
  title: string;
  description: string;
  priority: "high" | "medium" | "low";
  page: string;
  pageLabel: string;
  iconName: "shield" | "target" | "star" | "globe" | "file-text" | "share2" | "search";
}

export function useRecommendations() {
  const { selectedLocationId } = useLocationStore();

  const { data: locations } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations?.[0]?.id;

  const { data: tasks = [] } = useQuery<AssistantTask[]>({
    queryKey: ["/api/assistant/tasks", locationId],
    enabled: !!locationId,
  });

  const { data: reviews = [] } = useQuery<Review[]>({
    queryKey: ["/api/reviews", locationId],
    enabled: !!locationId,
  });

  const { data: citations = [] } = useQuery<Citation[]>({
    queryKey: ["/api/citations", locationId],
    enabled: !!locationId,
  });

  const { data: profileData } = useQuery<{ score: number; fields: { status: string }[] }>({
    queryKey: ["/api/gbp/profile-completeness", locationId],
    enabled: !!locationId,
  });

  const { data: stats } = useQuery<{
    tasksCompleted: number;
    postsPublished: number;
    reviewsReplied: number;
    hoursSaved: number;
    autopilotActions: number;
  }>({
    queryKey: ["/api/assistant/stats", locationId],
    enabled: !!locationId,
  });

  const pendingGbpTasks = tasks.filter(t =>
    ["category", "description", "service", "attributes"].includes(t.taskType) && t.status === "pending"
  ).length;

  const unrepliedReviews = reviews.filter(r => !r.reply).length;
  const unsyncedCitations = citations.filter(c => c.status !== "synced").length;
  const profileScore = profileData?.score ?? null;
  const missingFields = profileData?.fields?.filter(f => f.status !== "complete").length ?? 0;

  const recommendations: Recommendation[] = [];

  if (profileScore !== null && profileScore < 80) {
    recommendations.push({
      title: `Profile is ${profileScore}% complete — ${missingFields} fields need attention`,
      description: "A complete profile ranks higher in local search. Fill in the missing fields to boost your visibility.",
      priority: profileScore < 50 ? "high" : "medium",
      page: "/profile-health",
      pageLabel: "Profile Essentials",
      iconName: "shield",
    });
  }

  if (pendingGbpTasks > 0) {
    recommendations.push({
      title: `${pendingGbpTasks} GBP optimization${pendingGbpTasks !== 1 ? "s" : ""} ready to review`,
      description: "Aria has identified improvements to your business category, description, services, or attributes.",
      priority: "high",
      page: "/gbp-optimization",
      pageLabel: "GBP Optimization",
      iconName: "target",
    });
  }

  if (unrepliedReviews > 0) {
    recommendations.push({
      title: `${unrepliedReviews} review${unrepliedReviews !== 1 ? "s" : ""} waiting for a reply`,
      description: "Responding to reviews quickly improves your local SEO and shows customers you care.",
      priority: unrepliedReviews > 3 ? "high" : "medium",
      page: "/reviews",
      pageLabel: "Reviews",
      iconName: "star",
    });
  }

  if (unsyncedCitations > 0) {
    recommendations.push({
      title: `${unsyncedCitations} citation${unsyncedCitations !== 1 ? "s" : ""} not synced`,
      description: "Consistent business info across directories is a key local ranking factor. Sync your listings.",
      priority: unsyncedCitations > 5 ? "medium" : "low",
      page: "/citations",
      pageLabel: "Citations",
      iconName: "globe",
    });
  }

  if ((stats?.postsPublished ?? 0) < 4) {
    recommendations.push({
      title: "Publish more GBP posts this month",
      description: "Businesses that post weekly to Google get 2x more profile views. Schedule your next post.",
      priority: "medium",
      page: "/posts",
      pageLabel: "Post Scheduler",
      iconName: "file-text",
    });
  }

  recommendations.push({
    title: "Connect social accounts for cross-posting",
    description: "Auto-publish your GBP posts to Facebook, Instagram, and LinkedIn to reach a wider audience.",
    priority: "low",
    page: "/social",
    pageLabel: "Social",
    iconName: "share2",
  });

  const sorted = recommendations.sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 };
    return order[a.priority] - order[b.priority];
  });

  return {
    recommendations: sorted,
    highCount: sorted.filter(r => r.priority === "high").length,
    stats,
  };
}
